/** One offered week: its calendar key and the fiscal year the grid shows it under. */
export interface GridWeek {
  yearWeek: number;
  fiscalYear: string;
}

/** The frame's two boundaries, calendar `YYYYWW`. */
export interface ForecastBoundaries {
  /** Where this frame's history stops; the training range ends on the last real week before it. */
  editableWeekFrom: number | null | undefined;
  /** The plan's forecast boundary; the horizon counts its fiscal years from here. */
  forecastWeekFrom: number | null | undefined;
}

export interface ForecastDefaults {
  trainWeekFrom: number;
  trainWeekTo: number;
  forecastWeekTo: number;
}

/**
 * Derives the load dialog's default training range and forecast horizon
 * from the offered weeks (ascending by `yearWeek` — the grid's fiscal years,
 * see `buildGridWeekOptions`). Training defaults to everything the grid
 * shows before the last closed actual week. The horizon defaults to the
 * last week of FY+2: the rest of the fiscal year the plan's forecast starts
 * in plus the next two whole years, ending on that year's W52 (or W53). The
 * years are read off the offered weeks rather than counted, and the horizon
 * caps at the grid's last week when the grid ends sooner.
 *
 * Two anchors, not one: `editable_week_from` shifts with the context and the
 * viewer (it pads to the row-authorized history), so it may cap the training
 * range but must not pick the fiscal year the horizon counts from —
 * `forecast_week_from` is the plan's boundary and does that.
 */
export function deriveForecastDefaults(
  { editableWeekFrom, forecastWeekFrom }: ForecastBoundaries,
  weeks: GridWeek[]
): ForecastDefaults | undefined {
  const first = weeks[0];
  if (!editableWeekFrom || !forecastWeekFrom || !first) return undefined;

  const descending = [...weeks].reverse();
  // editable_week_from is where history ends and the editable/forecast
  // range starts — the last *actual* week is whatever real week precedes it.
  const trainWeekTo =
    descending.find((week) => week.yearWeek < editableWeekFrom) ?? first;
  // The forecast starts in the fiscal year of the plan's boundary week, so a
  // horizon set on W01 still covers that whole year and the two after it.
  const currentYear = (
    weeks.find((week) => week.yearWeek >= forecastWeekFrom) ?? trainWeekTo
  ).fiscalYear;
  const years = Array.from(new Set(weeks.map((week) => week.fiscalYear)));
  const targetYear =
    years[years.indexOf(currentYear) + 2] ?? years[years.length - 1];
  const forecastWeekTo =
    descending.find((week) => week.fiscalYear === targetYear) ?? descending[0];

  return {
    trainWeekFrom: first.yearWeek,
    trainWeekTo: trainWeekTo.yearWeek,
    forecastWeekTo: forecastWeekTo.yearWeek
  };
}
